import { createStore } from '@ngneat/elf'
import {
  addEntities,
  deleteEntities,
  deleteEntitiesByPredicate,
  getAllEntitiesApply,
  selectAllEntities,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities'
import { Injectable } from '@angular/core'
import { Article } from '../models/article'
import { ArticleDao } from '../services/dao/article.dao'
import { Doka360ProjectSelectorService } from '../services/doka360-project-selector.service'

const articleStore = createStore({ name: 'articles' }, withEntities<Article>())

@Injectable({
  providedIn: 'root',
})
export class ArticleRepository {
  public readonly articles$ = articleStore.pipe(selectAllEntities())

  constructor(
    private readonly articleDao: ArticleDao,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {}

  public async create(article: Article): Promise<number> {
    article.id = (await this.articleDao.createArticles([article], article.stockId))[0]
    articleStore.update(addEntities(article))
    return article.id
  }

  public async createAll(articles: Article[], stockId: number): Promise<Article[]> {
    const ids = await this.articleDao.createArticles(articles, stockId)
    articles.forEach((article, index) => {
      article.id = ids[index]
    })
    articleStore.update(addEntities(articles))

    return articles
  }

  public async delete(article: Article): Promise<void> {
    await this.articleDao.delete(article.id)
    articleStore.update(deleteEntities(article.id))
  }

  public async update(article: Article): Promise<void> {
    await this.articleDao.update(article)
    articleStore.update(updateEntities(article.id, article))
  }

  public async updateAll(articles: Article[], stockId: number): Promise<void> {
    await this.articleDao.updateAll(articles, stockId)
    articleStore.update(upsertEntities(articles))
  }

  public updateStockArticles(stockId: number, articles?: Article[]): void {
    if (!articles) {
      articleStore.update(deleteEntitiesByPredicate((e) => e.stockId === stockId))
      return
    }

    articleStore.update(upsertEntities(articles))
  }

  public async findAllByStockId(stockId: number, forceFetch: boolean): Promise<Article[]> {
    let articles = articleStore.query(
      getAllEntitiesApply({
        filterEntity: (e) => e.stockId === stockId,
      })
    )

    if (forceFetch || !articles || articles.length === 0) {
      const currentDoka360ProjectUid =
        this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()
      articles = await this.articleDao.findAllByStockId(stockId, currentDoka360ProjectUid)
      articleStore.update(upsertEntities(articles))
    }

    return articles
  }
}
